import { computed, ref } from 'vue';

import { getDayHash, useProxyModel } from '@components/composable';
import defaultLang from '@components/locale/ru';
import { pad, propsFactory, splitDate } from '@components/utils';

import type { IDatetimeProps } from '@components/composable';
import type { IRawDate, TDateLocale } from '@components/utils';
import type { PropType, Ref } from 'vue';

export interface ITimeProps {
  modelValue: IDatetimeProps['modelValue'];
  format12h: boolean;
  nowBtn: boolean;
  withSeconds: boolean;
  defaultDate?: string;
  allowedTime?: (hr: number | null, min: number | null, sec: number | null) => boolean;
  allowedHours?: number[];
  allowedMinutes?: number[];
  allowedSeconds?: number[];
}

export const makeTimeProps = propsFactory(
  {
    /**
     * Добавляет секунды в выбор времени
     */
    withSeconds: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отображает время в 12-часовом формате AM/PM
     */
    format12h: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отображает кнопку "Сейчас"
     */
    nowBtn: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Используемая дата по умолчанию, когда модель не заполнена (в формате YYYY/MM/DD)
     */
    defaultDate: {
      type: String as PropType<string>,
      validator: (value: string) => /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(value)
    },
    /**
     * Позволяет настроить какое время доступно для выбора
     */
    allowedTime: {
      type: Function as PropType<(hr: number | null, min: number | null, sec: number | null) => boolean>,
      default: undefined
    },
    /**
     * Позволяет настроить какие часы доступны для выбора
     */
    allowedHours: {
      type: Array as PropType<number[]>,
      default: undefined
    },
    /**
     * Позволяет настроить какие минуты доступны для выбора
     */
    allowedMinutes: {
      type: Array as PropType<number[]>,
      default: undefined
    },
    /**
     * Позволяет настроить какие секунды доступны для выбора
     */
    allowedSeconds: {
      type: Array as PropType<number[]>,
      default: undefined
    }
  },
  'time'
);

export function useTime(
  props: ITimeProps,
  internalLocale: Ref<TDateLocale>,
  internalMask: Ref<string>,
  getCurrentDate: (dateOnly?: boolean) => IRawDate
) {
  const defaultDateModel = computed(() => getDefaultDateModel());
  const model = useProxyModel(props, 'modelValue', '', (value) =>
    splitDate(value as string, internalMask.value, internalLocale.value, defaultDateModel.value)
  );
  const isAM = ref(model.value.hour === null || (model.value.hour as number) < 12);
  const internalFormat12h = computed(() => (props.format12h ? props.format12h : !defaultLang.date.format24h));

  const stringModel = computed(() => {
    const time = model.value;

    let hour;
    if (time.hour === null) {
      hour = '--';
    } else {
      if (time.hour !== undefined) {
        if (internalFormat12h.value) {
          if (isAM.value) {
            hour = String(time.hour === 0 ? 12 : time.hour);
          } else {
            hour = String(time.hour > 12 ? time.hour - 12 : time.hour);
          }
        } else {
          hour = pad(time.hour);
        }
      }
    }

    const minute = time.minute === null ? '--' : pad(time.minute as number);
    const second = time.second === null ? '--' : pad(time.second as number);

    return {
      hour,
      minute,
      second
    };
  });

  const hourInSelection = computed(() =>
    props.allowedHours
      ? (value: number) => props.allowedHours?.includes(value)
      : props.allowedTime
      ? (value: number) => props.allowedTime?.(value, null, null)
      : null
  );
  const minuteInSelection = computed(() =>
    props.allowedMinutes
      ? (value: number) => props.allowedMinutes?.includes(value)
      : props.allowedTime
      ? (value: number) => props.allowedTime?.(model.value.hour as number, value, null)
      : null
  );
  const secondInSelection = computed(() =>
    props.allowedSeconds
      ? (value: number) => props.allowedSeconds?.includes(value)
      : props.allowedTime
      ? (value: number) => props.allowedTime?.(model.value.hour as number, model.value.minute as number, value)
      : null
  );

  const validHours = computed(() => {
    if (!hourInSelection.value) return null;

    const am = getValidValues(0, 11, hourInSelection.value);
    const pm = getValidValues(12, 11, hourInSelection.value);

    return {
      am,
      pm,
      values: am.values.concat(pm.values)
    };
  });

  const validMinutes = computed(() =>
    minuteInSelection.value ? getValidValues(0, 59, minuteInSelection.value) : null
  );

  const validSeconds = computed(() =>
    secondInSelection.value ? getValidValues(0, 59, secondInSelection.value) : null
  );

  function getCurrentTime() {
    const date = new Date();
    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: date.getMilliseconds()
    };
  }

  function getDefaultDateModel() {
    if (typeof props.defaultDate !== 'string') {
      const date = getCurrentDate(true);
      date.dateHash = getDayHash(date);
      return date;
    }

    return splitDate(props.defaultDate, 'YYYY/MM/DD');
  }

  function getValidValues(start: number, count: number, testFn: Function) {
    const values = [...new Array(count + 1)]
      .map((_, index) => {
        const i = index + start;
        return { index: i, value: testFn(i) === true };
      })
      .filter((v) => v.value)
      .map((v) => v.index);

    return {
      min: values[0],
      max: values[values.length - 1],
      values,
      threshold: count + 1
    };
  }

  return {
    model,
    isAM,
    stringModel,
    hourInSelection,
    minuteInSelection,
    secondInSelection,
    validHours,
    validMinutes,
    validSeconds,
    getCurrentTime
  };
}

/**
 * Если передать массив чисел и число, которое меньше наименьшего числа в массиве, то вернется наибольшее число из массива и наоборот
 * @param numbers
 * @param target
 */
export function findClosestExtremeNumber(numbers: number[], target: number): number {
  if (target > Math.max(...numbers)) {
    return Math.min(...numbers);
  } else if (target < Math.min(...numbers)) {
    return Math.max(...numbers);
  } else {
    let closest = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      if (Math.abs(numbers[i] - target) < Math.abs(closest - target)) {
        closest = numbers[i];

        if (closest > target && numbers[numbers.length - 1] === closest) {
          closest = numbers[i - 1];
        }
      } else {
        if (closest === target) {
          return closest;
        }

        closest = closest < target ? numbers[i] : target > numbers[i - 1] ? numbers[i - 1] : numbers[0];

        return closest;
      }
    }
    return closest;
  }
}
